import { SagaIterator } from '@redux-saga/types';
import { Action } from 'common/interfaces/root-store';
import {
  initWithHashError,
  initWithHashSuccess,
  setCompleteSignUp,
  setLoaderActiveAction,
  setLoaderInactiveAction,
} from 'common/store/actions';
import { signUpApi } from 'modules/sign-up/service/settings.service';
import { signUpActions, signUpActionTypes as T } from 'modules/sign-up/store/actions';
import { all, call, put, takeLatest } from 'redux-saga/effects';

function* postSignUpStep({ payload, meta }: Action) {
  try {
    yield all(
      payload.actions.map((action: any) =>
        call(function* () {
          // eslint-disable-next-line no-useless-catch
          try {
            yield put(action);
          } catch (err: any) {
            throw err;
          }
        })
      )
    );
    meta.onSuccess();
  } catch (err: any) {
    meta.onError(err?.message || 'Step failed');
  }
}

function* postBusinessInformation({ payload, meta }: Action) {
  try {
    yield call(signUpApi.postBusinessInformation, payload);
    yield put(
      signUpActions.setUserInfo({
        first_name: payload.data.first_name,
        last_name: payload.data.last_name,
      })
    );
    if (meta?.onSuccess) meta.onSuccess();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (meta?.onError) meta.onError(error.response.data.message);
  }
}

function* postCompanyInformation({ payload, meta }: Action) {
  try {
    yield call(signUpApi.postCompanyInformation, payload);
    if (meta?.onSuccess) meta.onSuccess();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (meta?.onError) meta.onError(error.response.data.message);
  }
}

function* postAdditionalInformation({ payload, meta }: Action) {
  try {
    yield call(signUpApi.postAdditionalInformation, payload);
    if (meta?.onSuccess) meta.onSuccess();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (meta?.onError) meta.onError(error.response.data.message);
  }
}

function* postAgreementInformation({ payload, meta }: Action) {
  try {
    yield call(signUpApi.postAgreementInformation, payload);
    if (meta?.onSuccess) meta.onSuccess();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (meta?.onError) meta.onError(error.response.data.message);
  }
}

function* postPaymentInformation({ payload, meta }: Action) {
  try {
    yield call(signUpApi.postPaymentInformation, payload);
    if (meta?.onSuccess) meta.onSuccess();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (meta?.onError) meta.onError(error.response.data.message);
  }
}

function* getWallet({ payload, meta }: Action) {
  try {
    yield call(signUpApi.getWallet, payload);
    meta.onComplete(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    meta.onComplete(false);
  }
}

function* createWallet({ payload, meta }: Action) {
  try {
    yield call(signUpApi.createWallet, payload);
    const { data } = yield call(signUpApi.getWallet, payload);
    yield put(signUpActions.addCardData(data.cards));
    meta.onEnd(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const { data } = yield call(signUpApi.getWallet, payload);
    yield put(signUpActions.addCardData(data.cards));
    meta.onEnd(false);
  }
}

function* getUserInfo({ payload, meta }: Action) {
  try {
    const { data } = yield call(signUpApi.getUserInfo, payload);
    sessionStorage.setItem('hash', payload);
    yield put(initWithHashSuccess({ ...data.user, is_verified: true }));
    meta.onGenerated();
    // yield call(signUpApi.removeSignUp, payload.hash);
  } catch (error) {
    yield put(initWithHashError());
    meta.onError();
    meta.onGenerated();
  }
}

function* completeSignUp({ payload, meta }: Action) {
  try {
    const { data } = yield call(signUpApi.completeSignUp, payload);
    yield put(initWithHashSuccess({ ...data.user, is_verified: true }));
    yield put(setCompleteSignUp(data.url));
    sessionStorage.removeItem('hash');
    sessionStorage.removeItem('selectedClientType');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (meta?.onSuccess) meta.onSuccess();
  } catch (error: any) {
    meta.onError(error.response.data.message);
  } finally {
    yield put(setLoaderInactiveAction());
  }
}

function* firstSignUpStepSaga({ payload, meta }: Action) {
  try {
    yield put(setLoaderActiveAction());
    yield call(signUpApi.updateInformation, payload);
    yield put(
      signUpActions.setUserInfo({
        first_name: payload.data.first_name,
        last_name: payload.data.last_name,
      })
    );

    if (meta?.onSuccess) meta.onSuccess();
  } catch (error: any) {
    meta.onError(error.response.data.message);
  } finally {
    yield put(setLoaderInactiveAction());
  }
}

function* secondSignupStepSaga({ payload, meta }: Action) {
  try {
    yield put(setLoaderActiveAction());
    yield call(signUpApi.postCompanyInformation, payload.postCompanyInformation);
    yield call(signUpApi.postAdditionalInformation, payload.postAdditionalInformation);

    if (meta?.onSuccess) meta.onSuccess();
  } catch (error: any) {
    meta.onError(error.response.data.message);
    yield put(setLoaderInactiveAction());
  }
}

function* postDraftFields({ payload, meta }: Action) {
  try {
    yield call(signUpApi.postDraftFields, payload);
    if (meta?.onSuccess) meta.onSuccess();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (meta?.onError) meta.onError(error.response.data.message);
  }
}

export function* signUpSaga(): SagaIterator {
  yield all([
    takeLatest(T.POST_SIGN_UP_STEP, postSignUpStep),
    takeLatest(T.POST_BUSINESS_INFORMATION, postBusinessInformation),
    takeLatest(T.POST_ASIN_INFORMATION, postCompanyInformation),
    takeLatest(T.POST_ADDITIONAL_INFORMATION, postAdditionalInformation),
    takeLatest(T.POST_CONTRACT_TERMS, postAgreementInformation),
    takeLatest(T.POST_PAYMENT_INFORMATION, postPaymentInformation),
    takeLatest(T.GET_WALLET, getWallet),
    takeLatest(T.CREATE_WALLET, createWallet),
    takeLatest(T.GET_USER_INFO, getUserInfo),
    takeLatest(T.COMPLETE_SIGN_UP, completeSignUp),
    takeLatest(T.FIRST_SIGNUP_STEP, firstSignUpStepSaga),
    takeLatest(T.SECOND_SIGNUP_STEP, secondSignupStepSaga),
    takeLatest(T.POST_DRAFT_FIELDS, postDraftFields),
  ]);
}
